import { supabase } from "@/integrations/supabase/client";
import {
  ActivityLibraryRow,
  GetActivityLibraryParams,
  SaveActivityToLibraryParams,
  PromoteActivityToAgencyParams,
  DemoteActivityFromAgencyParams,
  AddLibraryActivityToItineraryParams,
  AddLibraryActivityToItineraryResult,
} from "@/integrations/supabase/rpc-types";
import {
  LibraryActivity,
  LibraryActivityFactory,
  LibraryActivityPayload,
  LibraryScope,
} from "@/lib/library/models/library-activity.model";

export interface ActivityLibraryFilters {
  activityType?: string;
  tags?: string[];
  search?: string;
  /** Ambito di lettura (personale/agenzia); null/undefined = tutte le righe visibili. */
  scope?: LibraryScope | null;
}

/**
 * Recupera le righe della libreria attività visibili all'utente corrente
 * (RLS già filtra per user/agency/admin), con filtro opzionale per tipo,
 * tag o ricerca testuale.
 */
export const fetchActivityLibrary = async (
  filters: ActivityLibraryFilters = {}
): Promise<LibraryActivity[]> => {
  try {
    const params: GetActivityLibraryParams = {
      p_activity_type: filters.activityType ?? null,
      p_tags: filters.tags ?? null,
      p_search: filters.search ?? null,
      p_scope: filters.scope ?? null,
    };

    // RPC non ancora presente nei tipi generati (types.ts va rigenerato dopo il deploy
    // della migration) — stesso workaround già usato in useFullProfile.ts / agencyDashboard.service.ts.
    const { data, error } = await supabase.rpc('get_activity_library' as any, params);

    if (error) {
      console.error('Error fetching activity library:', error);
      throw error;
    }

    return ((data ?? []) as unknown as ActivityLibraryRow[]).map((row) =>
      LibraryActivityFactory.fromRow(row)
    );
  } catch (error) {
    console.error('Unexpected error fetching activity library:', error);
    throw error;
  }
};

/**
 * Upsert di un'attività nella libreria. `owner_id` è forzato server-side ad
 * auth.uid(); `agencyId` valorizzato salva la riga nella libreria di quell'agenzia
 * (setta agency_id), null la salva come personale. Se `activity.id` corrisponde a una
 * entry esistente e modificabile la RPC esegue un update, altrimenti un insert.
 */
export const saveActivityToLibrary = async (
  activity: LibraryActivityPayload,
  agencyId: string | null
): Promise<LibraryActivity> => {
  try {
    const params: SaveActivityToLibraryParams = {
      p_activity: activity as unknown as SaveActivityToLibraryParams['p_activity'],
      p_agency_id: agencyId,
    };

    const { data, error } = await supabase.rpc('save_activity_to_library' as any, params);

    if (error || !data) {
      console.error('Error saving activity to library:', error);
      throw error ?? new Error('save_activity_to_library returned no data');
    }

    return LibraryActivityFactory.fromRow(data as unknown as ActivityLibraryRow);
  } catch (error) {
    console.error('Unexpected error saving activity to library:', error);
    throw error;
  }
};

/**
 * Promuove una riga personale a libreria agenzia (setta agency_id = la mia agenzia).
 * La riga resta comunque visibile anche in libreria personale. La RPC ricava l'agenzia
 * lato server dall'utente chiamante.
 */
export const promoteActivityToAgency = async (id: string): Promise<LibraryActivity> => {
  try {
    const params: PromoteActivityToAgencyParams = { p_id: id };

    const { data, error } = await supabase.rpc('promote_activity_to_agency' as any, params);

    if (error || !data) {
      console.error('Error promoting activity to agency library:', error);
      throw error ?? new Error('promote_activity_to_agency returned no data');
    }

    return LibraryActivityFactory.fromRow(data as unknown as ActivityLibraryRow);
  } catch (error) {
    console.error('Unexpected error promoting activity to agency library:', error);
    throw error;
  }
};

/**
 * Rimuove una riga dalla libreria agenzia (setta agency_id = null), rendendola di
 * nuovo solo personale. Inverso di `promoteActivityToAgency`. La RPC agisce solo sulle
 * righe di cui l'utente è il creatore (`owner_id = auth.uid()`), la RLS UPDATE resta la
 * fonte di verità sui permessi.
 */
export const demoteActivityFromAgency = async (id: string): Promise<LibraryActivity> => {
  try {
    const params: DemoteActivityFromAgencyParams = { p_id: id };

    const { data, error } = await supabase.rpc('demote_activity_from_agency' as any, params);

    if (error || !data) {
      console.error('Error demoting activity from agency library:', error);
      throw error ?? new Error('demote_activity_from_agency returned no data');
    }

    return LibraryActivityFactory.fromRow(data as unknown as ActivityLibraryRow);
  } catch (error) {
    console.error('Unexpected error demoting activity from agency library:', error);
    throw error;
  }
};

/**
 * Elimina una entry dalla libreria attività. La RLS DELETE su `activity_library`
 * resta l'unica fonte di verità sui permessi: tipicamente si può cancellare solo
 * una riga di cui si è creatori (`owner_id = me`), una riga della propria agenzia
 * se si è l'account agenzia, o qualunque riga se admin.
 * Qui non serve quindi una RPC dedicata: la tabella non è ancora nei tipi generati
 * (types.ts va rigenerato dopo il deploy della migration), stesso workaround `as any`
 * già usato per le RPC in questo file.
 */
export const deleteActivityFromLibrary = async (id: string): Promise<void> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tabella non ancora nei tipi generati Database, vedi commento sopra
    const { error } = await supabase.from('activity_library' as any).delete().eq('id', id);

    if (error) {
      console.error('Error deleting activity from library:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error deleting activity from library:', error);
    throw error;
  }
};

/**
 * Copia una riga di libreria in una nuova attività dell'itinerario, in una data specifica.
 */
export const addLibraryActivityToItinerary = async (
  libraryActivityId: string,
  itineraryId: string,
  date: string
): Promise<AddLibraryActivityToItineraryResult> => {
  try {
    const params: AddLibraryActivityToItineraryParams = {
      p_library_activity_id: libraryActivityId,
      p_itinerary_id: itineraryId,
      p_date: date,
    };

    const { data, error } = await supabase.rpc('add_library_activity_to_itinerary' as any, params);

    if (error || !data) {
      console.error('Error adding library activity to itinerary:', error);
      throw error ?? new Error('add_library_activity_to_itinerary returned no data');
    }

    return data as unknown as AddLibraryActivityToItineraryResult;
  } catch (error) {
    console.error('Unexpected error adding library activity to itinerary:', error);
    throw error;
  }
};
